import React, { useState, useRef, useEffect } from "react";
// MODIFICATION: Import createPortal here as well for the mobile preview. Removed createRoot as it's no longer needed here.
import { createPortal } from "react-dom";
import Icons from "./Icons";
import { SECTION_TEMPLATES } from "./constants";
import EditorSidebar from "./components/EditorSidebar";
import ResumePreview from "./components/ResumePreview";
import FullscreenModal from "./components/FullscreenModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./ExecutivePremiumTemplate.css";
import "./sections.css";
import "./MinimalistPremiumTemplate.css";
import "./CreativePremiumTemplate.css";
import "./ImprovedEditor.css";

// Define available premium template options
export const PREMIUM_TEMPLATE_OPTIONS = [
  {
    id: "minimalist",
    name: "Minimalist Modern",
    description: "Clean single-column design with modern typography",
    icon: <Icons.FileText className="w-5 h-5" />,
    preview: "minimalist",
  },
  {
    id: "creative",
    name: "Creative Bold",
    description: "Stand-out design with colored sidebar",
    icon: <Icons.Lightbulb className="w-5 h-5" />,
    preview: "creative",
  },
  {
    id: "executive",
    name: "Executive Two-Column",
    description: "Professional layout with sidebar for skills and education",
    icon: <Icons.Menu className="w-5 h-5" />,
    preview: "executive",
  },
];

// Navigation sections
export const NAVIGATION_SECTIONS = [
  {
    id: "header",
    label: "Contact Info",
    icon: <Icons.User className="w-5 h-5" />,
  },
  {
    id: "main",
    label: "Experience",
    icon: <Icons.Briefcase className="w-5 h-5" />,
  },
  {
    id: "sidebar",
    label: "Skills & Education",
    icon: <Icons.GraduationCap className="w-5 h-5" />,
  },
];

const ExecutivePremiumTemplate = ({ data, onUpdateData }) => {
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [activePanel, setActivePanel] = useState("header");
  const [expandedSections, setExpandedSections] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showAddSectionMenu, setShowAddSectionMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenTab, setFullscreenTab] = useState("header");
  const [showFullscreenAddMenu, setShowFullscreenAddMenu] = useState(false);
  const [mobileView, setMobileView] = useState("editor"); // "editor" or "preview"
  const [isMobile, setIsMobile] = useState(false);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const dropdownRef = useRef(null);
  const templateDropdownRef = useRef(null);
  const fullscreenDropdownRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAddSectionMenu(false);
      }
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(event.target)
      ) {
        setShowTemplateDropdown(false);
      }
      if (
        fullscreenDropdownRef.current &&
        !fullscreenDropdownRef.current.contains(event.target)
      ) {
        setShowFullscreenAddMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle escape key for fullscreen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleEscape);
      document.body.classList.add("fullscreen-open");
      document.body.style.overflow = "hidden";
    } else {
      document.body.classList.remove("fullscreen-open");
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.classList.remove("fullscreen-open");
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  // Show hint when entering preview mode
  useEffect(() => {
    if (isMobile && mobileView === "preview") {
      setShowHint(true);
      const timer = setTimeout(() => setShowHint(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, mobileView]);

  // Helper function to ensure all sections have IDs
  const ensureSectionIds = (sections) => {
    if (!Array.isArray(sections)) return [];

    return sections.map((section, index) => ({
      ...section,
      id:
        section.id ||
        `section_${Date.now()}_${index}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      items: Array.isArray(section.items)
        ? section.items.map((item, itemIndex) => {
            if (typeof item === "string") return item;
            return {
              ...item,
              id:
                item.id ||
                `item_${Date.now()}_${itemIndex}_${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
            };
          })
        : [],
    }));
  };

  // Initialize data with proper structure
  const initializeResumeData = (inputData) => {
    const baseData = {
      header: {
        name: "",
        subtitle: "",
        tagline: "",
        contactInfo: [],
      },
      mainContent: [],
      sidebarContent: [],
      selectedTemplate: "minimalist",
    };

    if (!inputData) return baseData;

    return {
      ...baseData,
      ...inputData,
      mainContent: ensureSectionIds(inputData.mainContent || []),
      sidebarContent: ensureSectionIds(inputData.sidebarContent || []),
    };
  };

  const resumeData = initializeResumeData(data);
  const selectedTemplate = resumeData.selectedTemplate || "minimalist";

  // Open fullscreen with specific tab
  const openFullscreen = (tab) => {
    setFullscreenTab(tab);
    setIsFullscreen(true);
    if (isMobile) {
      setMobileView("editor");
    }
  };

  // Close fullscreen and show preview on mobile
  const closeFullscreenAndPreview = () => {
    setIsFullscreen(false);
    if (isMobile) {
      setMobileView("preview");
    }
  };

  // Handle mobile fullscreen preview
  const openMobileFullscreenPreview = () => {
    setShowFullscreenPreview(true);
    document.body.style.overflow = "hidden";
  };

  const closeMobileFullscreenPreview = () => {
    setShowFullscreenPreview(false);
    document.body.style.overflow = "";
  };

  // Calculate progress
  const calculateProgress = () => {
    let completed = 0;
    let total = 0;

    if (resumeData.header.name) completed++;
    total++;
    if (resumeData.header.subtitle) completed++;
    total++;
    if (resumeData.header.contactInfo?.length > 0) completed++;
    total++;
    if (resumeData.mainContent?.length > 0) completed++;
    total++;
    if (resumeData.sidebarContent?.length > 0) completed++;
    total++;

    return Math.round((completed / total) * 100);
  };

  const toggleSectionExpansion = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const updateData = (newData) => {
    if (onUpdateData) {
      const updatedData = {
        ...newData,
        mainContent: ensureSectionIds(newData.mainContent || []),
        sidebarContent: ensureSectionIds(newData.sidebarContent || []),
      };
      onUpdateData(updatedData);
    }
  };

  const handleTemplateChange = (templateId) => {
    updateData({
      ...resumeData,
      selectedTemplate: templateId,
    });
    setShowTemplateDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportError(null);

    const printContainer = document.createElement("div");
    printContainer.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 816px;
      background-color: white;
    `;
    document.body.appendChild(printContainer);

    // We cannot use the createRoot API here because it doesn't provide a callback
    // to know when rendering is complete, which is necessary for html2canvas.
    // ReactDOM.render is still available for this use case.
    const ReactDOM = await import("react-dom");

    try {
      await new Promise((resolve) => {
        ReactDOM.render(
          <ResumePreview
            resumeData={resumeData}
            selectedTemplate={selectedTemplate}
          />,
          printContainer,
          resolve
        );
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      await Promise.all(
        Array.from(printContainer.getElementsByTagName("img"))
          .filter((img) => !img.complete)
          .map(
            (img) =>
              new Promise((resolve) => {
                img.onload = img.onerror = resolve;
              })
          )
      );

      const resumeContentElement = printContainer.querySelector(
        '[class*="-resume-template"], .resume-paper'
      );

      if (!resumeContentElement) {
        throw new Error("Could not find resume content to render.");
      }

      const canvas = await html2canvas(resumeContentElement, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const A4_WIDTH_MM = 210;
      const A4_HEIGHT_MM = 297;
      const MARGIN_MM = 15;

      const contentWidth = A4_WIDTH_MM - MARGIN_MM * 2;
      const contentHeight = A4_HEIGHT_MM - MARGIN_MM * 2;

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const scaledImgHeight = (imgHeight * contentWidth) / imgWidth;
      const totalPages = Math.ceil(scaledImgHeight / contentHeight);

      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();

        const sourceY = (contentHeight * i * imgWidth) / contentWidth;
        const sourceHeight = Math.min(
          (contentHeight * imgWidth) / contentWidth,
          imgHeight - sourceY
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = sourceHeight;
        const pageCtx = pageCanvas.getContext("2d");

        pageCtx.drawImage(
          canvas,
          0,
          sourceY,
          imgWidth,
          sourceHeight,
          0,
          0,
          imgWidth,
          sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL("image/png", 1.0);
        pdf.addImage(
          pageImgData,
          "PNG",
          MARGIN_MM,
          MARGIN_MM,
          contentWidth,
          (sourceHeight * contentWidth) / imgWidth
        );
      }

      if (totalPages > 1) {
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.text(
            `Page ${i} of ${totalPages}`,
            A4_WIDTH_MM / 2,
            A4_HEIGHT_MM - 10,
            { align: "center" }
          );
        }
      }

      const name = resumeData.header?.name || "Resume";
      const date = new Date().toISOString().split("T")[0];
      const filename = `${name.replace(/\s+/g, "_")}_Resume_${date}.pdf`;

      pdf.setProperties({
        title: `${name} - Professional Resume`,
        author: name,
      });
      pdf.save(filename);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setExportError(
        error.message || "Failed to generate PDF. Please try again."
      );
    } finally {
      ReactDOM.unmountComponentAtNode(printContainer);
      document.body.removeChild(printContainer);
      setIsExporting(false);
    }
  };

  // Section management functions
  const updateSection = (location, sectionId, updates) => {
    const sections = resumeData[location] || [];
    const newSections = sections.map((section) =>
      section.id === sectionId
        ? {
            ...section,
            ...updates,
            items:
              updates.items !== undefined ? updates.items : section.items || [],
          }
        : section
    );
    updateData({ ...resumeData, [location]: newSections });
  };

  const addSection = (location, sectionType) => {
    const templates =
      location === "mainContent"
        ? SECTION_TEMPLATES.main
        : SECTION_TEMPLATES.sidebar;
    const template = templates[sectionType];

    if (!template) {
      console.error(`Template not found for section type: ${sectionType}`);
      return;
    }

    const newSection = {
      ...template,
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      items: Array.isArray(template.items)
        ? template.items.map((item, index) => {
            if (typeof item === "string") return item;
            return {
              ...item,
              id: `item_${Date.now()}_${index}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
            };
          })
        : [],
    };

    updateData({
      ...resumeData,
      [location]: [...resumeData[location], newSection],
    });

    setShowAddSectionMenu(false);
    setExpandedSections((prev) => ({
      ...prev,
      [newSection.id]: true,
    }));
  };

  const removeSection = (location, sectionId) => {
    updateData({
      ...resumeData,
      [location]: resumeData[location].filter(
        (section) => section.id !== sectionId
      ),
    });
  };

  const moveSection = (location, index, direction) => {
    const sections = [...resumeData[location]];
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex >= 0 && newIndex < sections.length) {
      [sections[index], sections[newIndex]] = [
        sections[newIndex],
        sections[index],
      ];
      updateData({
        ...resumeData,
        [location]: sections,
      });
    }
  };

  const addSectionItem = (location, sectionId, itemTemplate) => {
    const sections = resumeData[location] || [];
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const newItem = {
      ...itemTemplate,
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    const newSections = [...sections];
    newSections[sectionIndex] = {
      ...section,
      items: [...(section.items || []), newItem],
    };

    updateData({
      ...resumeData,
      [location]: newSections,
    });
  };

  const updateSectionItem = (location, sectionId, itemId, updates) => {
    const sections = resumeData[location] || [];
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    if (!Array.isArray(section.items)) section.items = [];

    const itemIndex = section.items.findIndex((item) => item.id === itemId);
    if (itemIndex === -1) return;

    const newSections = [...sections];
    newSections[sectionIndex] = {
      ...section,
      items: section.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    };

    updateData({
      ...resumeData,
      [location]: newSections,
    });
  };

  const removeSectionItem = (location, sectionId, itemId) => {
    const sections = resumeData[location] || [];
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const newSections = [...sections];
    newSections[sectionIndex] = {
      ...section,
      items: section.items.filter((item) => item.id !== itemId),
    };

    updateData({
      ...resumeData,
      [location]: newSections,
    });
  };

  return (
    <>
      <style>{`
        /* Mobile Styles */
        @media (max-width: 768px) {
          .executive-premium-editor {
            flex-direction: column;
            height: 100vh;
            overflow: hidden;
          }

          .editor-sidebar {
            width: 100% !important;
            height: 100vh;
            display: ${isMobile && mobileView === "preview" ? "none" : "flex"};
          }

          .resume-preview-container {
            width: 100% !important;
            height: 100vh;
            display: ${isMobile && mobileView === "editor" ? "none" : "block"};
            position: relative;
            overflow: hidden;
            background: #f3f4f6;
          }

          .mobile-preview-header {
            position: sticky;
            top: 0;
            z-index: 100;
            background: linear-gradient(135deg, #065f46 0%, #047857 100%);
            padding: 10px 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          .mobile-preview-title {
            color: white;
            font-size: 14px;
            font-weight: 600;
          }

          .mobile-preview-actions {
            display: flex;
            gap: 6px;
          }

          .mobile-preview-btn {
            padding: 8px 12px;
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            color: white;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 4px;
            transition: all 0.2s;
          }

          .mobile-preview-btn:hover {
            background: rgba(255, 255, 255, 0.3);
          }

          .mobile-preview-btn.primary {
            background: white;
            color: #065f46;
          }

          .mobile-preview-btn.primary:hover {
            background: #f0f0f0;
          }

          .mobile-preview-btn svg {
            width: 16px;
            height: 16px;
          }

          .mobile-preview-btn span {
            font-size: 12px;
            white-space: nowrap;
          }

          /* Mobile Preview Wrapper - Shows actual resume size */
          .mobile-preview-wrapper {
            position: relative;
            width: 100%;
            height: calc(100vh - 50px); /* Container height */
            padding: 0;
            overflow-y: scroll; /* Force scroll */
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            background: #f3f4f6;
          }

          .mobile-resume-preview {
            background: white;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
            border-radius: 0;
            overflow: visible !important; /* Critical: allow content overflow */
            cursor: pointer;
            transition: transform 0.2s;
            position: relative;
            width: 100%;
            max-width: 100%;
            height: auto !important; /* Let height be natural */
            min-height: 200vh; /* Ensure enough height for scrolling */
          }

          .mobile-resume-preview:active {
            transform: scale(0.98);
          }

          .mobile-resume-content {
            position: relative;
            width: 100%;
            height: auto !important;
            transform: none;
            pointer-events: none;
            overflow: visible !important; /* Allow content to expand */
          }

          .mobile-resume-content > * {
            transform: scale(var(--preview-scale, 1));
            transform-origin: top left;
            width: calc(100% / var(--preview-scale, 1));
            height: auto !important;
            overflow: visible !important;
          }

          /* Override any padding from child components on mobile */
          .mobile-resume-content .resume-paper {
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            width: 100% !important;
            height: auto !important;
            min-height: 100vh !important;
            overflow: visible !important;
          }

          .mobile-resume-content .minimalist-resume,
          .mobile-resume-content .creative-resume,
          .mobile-resume-content .tae-resume {
            width: 100% !important;
            height: auto !important;
            min-height: 150vh !important; /* Ensure minimum height */
            box-sizing: border-box !important;
            padding: calc(24px * var(--preview-scale, 1)) !important;
            overflow: visible !important;
            position: relative !important;
          }
          
          /* Ensure all nested containers allow overflow */
          .mobile-resume-content .resume-preview-container,
          .mobile-resume-content .preview-content,
          .mobile-resume-content .minimalist-body,
          .mobile-resume-content .creative-main,
          .mobile-resume-content .creative-sidebar {
            overflow: visible !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }
          
          /* Ensure minimalist body uses full width */
          .mobile-resume-content .minimalist-body {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Minimalist sections full width */
          .mobile-resume-content .minimalist-section {
            width: 100% !important;
          }

          .mobile-preview-hint {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.7);
            color: white;
            border-radius: 20px;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            pointer-events: none;
            animation: fadeInOut 3s ease-in-out;
          }

          @keyframes fadeInOut {
            0% { opacity: 0; }
            20% { opacity: 1; }
            80% { opacity: 1; }
            100% { opacity: 0; }
          }

          /* Fullscreen Preview Modal */
          .mobile-fullscreen-preview {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: #f5f5f5;
            z-index: 10000;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            height: 100vh;
            width: 100vw;
          }

          .mobile-fullscreen-header {
            background: rgba(0, 0, 0, 0.9);
            padding: 12px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative; /* Changed back to relative */
            z-index: 10001;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
            flex-shrink: 0; /* Prevent header from shrinking */
          }

          .mobile-fullscreen-close {
            padding: 8px 16px;
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            color: white;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;
          }

          .mobile-fullscreen-close:active {
            background: rgba(255, 255, 255, 0.3);
          }

          .mobile-fullscreen-body {
            flex: 1;
            overflow-y: scroll !important; /* Force scroll */
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            padding: 20px;
            background: #f5f5f5;
            width: 100%;
            position: relative;
            display: block; /* Change to block */
          }

          .mobile-fullscreen-resume {
            background: white;
            width: 100%;
            max-width: 850px;
            height: auto !important; /* Natural height */
            min-height: 200vh !important; /* Ensure scrollable height */
            transform: none;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            margin: 0 auto 40px auto;
            position: relative;
            overflow: visible !important;
          }

          .mobile-fullscreen-resume .resume-paper {
            width: 100% !important;
            height: auto !important;
            min-height: 150vh !important;
            transform: none !important;
            transform-origin: top center !important;
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: white !important;
            position: relative !important;
            overflow: visible !important;
          }

          /* Ensure resume content uses proper padding */
          .mobile-fullscreen-resume .minimalist-resume,
          .mobile-fullscreen-resume .creative-resume,
          .mobile-fullscreen-resume .tae-resume {
            width: 100% !important;
            height: auto !important;
            min-height: 150vh !important; /* Ensure scrollable content */
            box-sizing: border-box !important;
            padding: 40px !important;
            margin: 0 !important;
            background: white !important;
            overflow: visible !important;
            position: relative !important;
          }
          
          /* Force all containers to allow natural height */
          .mobile-fullscreen-resume .resume-preview-container,
          .mobile-fullscreen-resume .preview-content,
          .mobile-fullscreen-resume .minimalist-body,
          .mobile-fullscreen-resume .creative-main,
          .mobile-fullscreen-resume .creative-sidebar,
          .mobile-fullscreen-resume * {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }

          .mobile-fullscreen-resume .resume-paper {
            width: 100% !important;
            transform: none !important; /* Remove scaling in fullscreen */
            transform-origin: top center !important;
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: white !important;
          }

          /* Ensure resume content uses proper padding */
          .mobile-fullscreen-resume .minimalist-resume,
          .mobile-fullscreen-resume .creative-resume,
          .mobile-fullscreen-resume .tae-resume {
            width: 100% !important;
            box-sizing: border-box !important;
            padding: 40px !important; /* Fixed padding, no scaling */
            margin: 0 !important;
            background: white !important;
          }
          
          /* Ensure minimalist content spans full width */
          .mobile-fullscreen-resume .minimalist-body {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .mobile-fullscreen-resume .minimalist-header {
            text-align: center !important;
            width: 100% !important;
          }
          
          .mobile-fullscreen-resume .minimalist-section {
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          
          .mobile-fullscreen-resume .minimalist-section-content {
            width: 100% !important;
          }
          
          /* Job headers should span full width */
          .mobile-fullscreen-resume .minimalist-job-header {
            display: flex !important;
            justify-content: space-between !important;
            width: 100% !important;
          }
          
          /* Remove any preview container constraints */
          .mobile-fullscreen-resume .preview-content,
          .mobile-fullscreen-resume .resume-preview-container {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            background: transparent !important;
          }
          
          /* On very small screens, reduce padding */
          @media (max-width: 400px) {
            .mobile-fullscreen-body {
              padding: 10px;
            }
            
            .mobile-fullscreen-resume .minimalist-resume,
            .mobile-fullscreen-resume .creative-resume,
            .mobile-fullscreen-resume .tae-resume {
              padding: 25px !important;
            }
          }

          .pinch-zoom-hint {
            color: white;
            font-size: 13px;
            font-weight: 400;
            opacity: 0.9;
            letter-spacing: 0.3px;
          }

          .pinch-zoom-hint {
            color: white;
            font-size: 12px;
            opacity: 0.8;
          }

          .resume-paper {
            transform: none !important;
            width: 100% !important;
            margin: 0 !important;
          }

          .preview-content {
            padding-bottom: 20px;
            background: transparent;
          }

          .template-selector-header {
            padding: 16px;
          }

          .editor-brand {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .fullscreen-edit-btn {
            width: 100%;
            justify-content: center;
          }

          .template-dropdown-container {
            margin-top: 12px !important;
          }

          .nav-tabs {
            padding: 0 16px;
          }

          .editor-content {
            padding: 16px;
            overflow-y: auto;
            flex: 1;
          }

          .editor-footer {
            padding: 16px;
            position: sticky;
            bottom: 0;
            background: white;
            border-top: 1px solid #e5e7eb;
            box-shadow: 0 -2px 8px rgba(0,0,0,0.05);
          }

          .export-pdf-btn {
            width: 100%;
            max-width: none;
          }
        }

        /* Fullscreen Mobile Styles */
        @media (max-width: 768px) {
          .fullscreen-container {
            width: 100%;
            height: 100vh;
            border-radius: 0;
          }

          .fullscreen-header {
            padding: 12px 16px;
          }

          .fullscreen-nav-tabs {
            flex-wrap: nowrap;
            overflow-x: auto;
            gap: 8px;
          }

          .fullscreen-nav-tab {
            flex-shrink: 0;
            padding: 8px 12px;
            font-size: 13px;
          }

          .fullscreen-close-btn {
            padding: 8px 12px;
            font-size: 13px;
          }

          .fullscreen-body {
            padding: 16px;
          }

          .fullscreen-add-section-btn {
            width: 100%;
            justify-content: center;
          }
        }

        .spinner-icon {
          display: inline-block;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .export-pdf-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .resume-paper {
          background-color: white;
          position: relative;
        }
        
        .resume-section {
          break-inside: avoid;
          position: relative;
        }
        
        .experience-item {
          break-inside: avoid;
        }

        /* Print styles for PDF export */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .resume-paper, .minimalist-resume, .creative-resume {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
          }
        }

        /* Ensure all resume containers have proper box-sizing */
        .resume-container *, .resume-paper *, .minimalist-resume *, .creative-resume * {
          box-sizing: border-box;
        }
      `}</style>

      <div className="executive-premium-editor">
        {/* Editor Sidebar - Hidden on mobile when viewing preview */}
        <EditorSidebar
          resumeData={resumeData}
          updateData={updateData}
          activePanel={activePanel}
          setActivePanel={setActivePanel}
          selectedTemplate={selectedTemplate}
          handleTemplateChange={handleTemplateChange}
          showTemplateDropdown={showTemplateDropdown}
          setShowTemplateDropdown={setShowTemplateDropdown}
          templateDropdownRef={templateDropdownRef}
          openFullscreen={openFullscreen}
          calculateProgress={calculateProgress}
          expandedSections={expandedSections}
          toggleSectionExpansion={toggleSectionExpansion}
          showAddSectionMenu={showAddSectionMenu}
          setShowAddSectionMenu={setShowAddSectionMenu}
          dropdownRef={dropdownRef}
          updateSection={updateSection}
          addSection={addSection}
          removeSection={removeSection}
          moveSection={moveSection}
          addSectionItem={addSectionItem}
          updateSectionItem={updateSectionItem}
          removeSectionItem={removeSectionItem}
          handleExportPDF={handleExportPDF}
          isExporting={isExporting}
          exportError={exportError}
          isMobile={isMobile}
          setMobileView={setMobileView}
        />

        <div className="resume-preview-container">
          {isMobile && mobileView === "preview" && (
            <>
              <div className="mobile-preview-header">
                <span className="mobile-preview-title">Resume Preview</span>
                <div className="mobile-preview-actions">
                  <button
                    onClick={() => setMobileView("editor")}
                    className="mobile-preview-btn"
                  >
                    <Icons.Edit className="w-4 h-4" />
                    <span>Back to Editor</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={isExporting}
                    className="mobile-preview-btn primary"
                  >
                    {isExporting ? (
                      <>
                        <span
                          className="spinner-icon"
                          style={{ fontSize: "12px" }}
                        >
                          ⟳
                        </span>
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Icons.Download className="w-4 h-4" />
                        <span>Export PDF</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile Preview with Tap to Fullscreen */}
              <div className="mobile-preview-wrapper">
                <div
                  className="mobile-resume-preview"
                  onClick={openMobileFullscreenPreview}
                  style={{
                    "--preview-scale": 0.9722921914357683,
                  }}
                >
                  <div className="mobile-resume-content">
                    <ResumePreview
                      resumeData={resumeData}
                      selectedTemplate={selectedTemplate}
                    />
                  </div>
                </div>
                {showHint && (
                  <div className="mobile-preview-hint">
                    <Icons.Maximize className="w-4 h-4" />
                    <span>Tap to view fullscreen</span>
                  </div>
                )}
              </div>

              {/* MODIFICATION: Fullscreen Preview Modal now uses a portal instead of createRoot */}
              {showFullscreenPreview &&
                createPortal(
                  <div className="mobile-fullscreen-preview">
                    <div className="mobile-fullscreen-header">
                      <span className="pinch-zoom-hint">
                        Pinch to zoom • Scroll to navigate
                      </span>
                      <button
                        onClick={closeMobileFullscreenPreview}
                        className="mobile-fullscreen-close"
                      >
                        <Icons.X className="w-4 h-4" />
                        <span>Close</span>
                      </button>
                    </div>
                    <div className="mobile-fullscreen-body">
                      <div className="mobile-fullscreen-resume">
                        <ResumePreview
                          resumeData={resumeData}
                          selectedTemplate={selectedTemplate}
                        />
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </>
          )}

          {/* Desktop Preview */}
          {!isMobile && (
            <ResumePreview
              resumeData={resumeData}
              selectedTemplate={selectedTemplate}
            />
          )}
        </div>
      </div>

      {/* MODIFICATION: Render the FullscreenModal directly. It handles its own visibility and portal logic. */}
      <FullscreenModal
        isFullscreen={isFullscreen}
        setIsFullscreen={setIsFullscreen}
        fullscreenTab={fullscreenTab}
        setFullscreenTab={setFullscreenTab}
        resumeData={resumeData}
        updateData={updateData}
        updateSection={updateSection}
        addSection={addSection}
        removeSection={removeSection}
        addSectionItem={addSectionItem}
        updateSectionItem={updateSectionItem}
        removeSectionItem={removeSectionItem}
        showFullscreenAddMenu={showFullscreenAddMenu}
        setShowFullscreenAddMenu={setShowFullscreenAddMenu}
        fullscreenDropdownRef={fullscreenDropdownRef}
        isMobile={isMobile}
        closeFullscreenAndPreview={closeFullscreenAndPreview}
      />
    </>
  );
};

// Add missing Icons if not present
Icons.Maximize =
  Icons.Maximize ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5h-4m4 0v-4"
      />
    </svg>
  ));

Icons.Eye =
  Icons.Eye ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ));

Icons.Edit =
  Icons.Edit ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  ));

Icons.Download =
  Icons.Download ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  ));

Icons.X =
  Icons.X ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  ));

export default ExecutivePremiumTemplate;
